import React, { FC, useState, useMemo } from 'react' // Importamos useMemo
import { TextInput } from 'components/TextInput'
import { SelectInput } from 'components/SelectInput'
import { useTranslation } from 'react-i18next'
import { UserRoles, User } from 'models/user'
import { Stack } from 'components/Stack'
import { Button } from 'components/Button'
import { Alert } from 'components/Alert'

export type UserFormFields = {
  firstName: string
  lastName: string
  email: string
  role: number
}

type UserFormProps = {
  onSubmit: (form: UserFormFields) => void
  currentUser: User
  user?: User
  status?: string
}

const roles = [UserRoles.OWNER, UserRoles.MANAGER, UserRoles.STAFF]

export const UserForm: FC<UserFormProps> = ({ currentUser, user, status, onSubmit }) => {
  const { t } = useTranslation()

  const [form, setForm] = useState<UserFormFields>({
    firstName: user ? user.firstName! : '',
    lastName: user ? user.lastName! : '',
    email: user ? user.email : '',
    role: user ? user.role : UserRoles.STAFF,
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit(form)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [event.target.name]: event.target.value })
  }

  // 1. MEMORIZAMOS LAS OPCIONES
  // Esto evita que Chrome piense que la lista cambió en cada render
  const options = useMemo(() => {
    return roles
      .filter((role) => currentUser.canCreateUserRole(role))
      .map((role) => ({
        label: t(`user.role.${role}`),
        value: role,
      }))
  }, [currentUser, t]) // Solo se recalcula si cambia el usuario o el idioma

  // 2. BUSCAMOS LA SELECCIÓN ACTUAL
  // Usamos undefined (no null) para evitar los errores rojos de TS
  const currentSelection = useMemo(() => 
    options.find((option) => option.value === form.role) || undefined,
  [options, form.role])

  return (
    <form onSubmit={handleSubmit}>
      <Stack space="medium">
        <TextInput
          name={'firstName'}
          label={t('user.invite.form.firstName')}
          value={form.firstName}
          onChange={handleChange}
          required
        />
        <TextInput
          name={'lastName'}
          label={t('user.invite.form.lastName')}
          value={form.lastName}
          onChange={handleChange}
          required
        />
        <TextInput
          name="email"
          label={t('user.invite.form.email')}
          value={form.email}
          onChange={handleChange}
          required
        />
        
        <SelectInput
          label={t('user.invite.form.role')}
          value={currentSelection}
          options={options}
          onChange={(roleOption) => {
            // GUARDIA: Solo actualizamos si el valor realmente cambió 
            // para evitar el doble disparo de Chrome/Downshift
            if (roleOption && roleOption.value !== form.role) {
              setForm({ ...form, role: Number(roleOption.value) })
            }
          }}
        />

        <Button type="submit" loading={status === 'loading'}>
          {user ? t('user.edit.save') : t('user.invite.form.cta')}
        </Button>
        
        {status === 'error' && (
          <Alert tone="critical" full>
            {t('common.messages.error')}
          </Alert>
        )}
        {status === 'success' && (
          <Alert tone="positive" full>
            {t('user.invite.success')}
          </Alert>
        )}
      </Stack>
    </form>
  )
}

UserForm.displayName = 'UserForm'